let audioContext = null;

function getAudioContext() {
  if (typeof window === 'undefined') {
    return null;
  }
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }
  if (!audioContext) {
    audioContext = new AudioContextClass();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
}

/** Call once after user interaction so opponent-move sounds can play. */
export function unlockMoveSounds() {
  getAudioContext();
}

function playMoveTone({
  frequency,
  volume = 0.045,
  duration = 0.09,
  type = 'triangle',
  delay = 0
}) {
  const audio = getAudioContext();
  if (!audio) {
    return;
  }

  const now = audio.currentTime + delay;
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  const filter = audio.createBiquadFilter();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, now);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1400, now);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  oscillator.connect(filter);
  filter.connect(gain);
  gain.connect(audio.destination);

  oscillator.start(now);
  oscillator.stop(now + duration + 0.02);
}

/** Soft click when you move a piece. */
export function playOwnMoveSound() {
  playMoveTone({ frequency: 520, volume: 0.05, duration: 0.08 });
}

/** Softer, lower tone when the opponent moves. */
export function playOpponentMoveSound() {
  playMoveTone({ frequency: 360, volume: 0.04, duration: 0.1 });
}

/** Percussive thud for captures — distinct from quiet moves and checks. */
export function playCaptureSound({ own = true } = {}) {
  playMoveTone({
    frequency: own ? 190 : 160,
    volume: 0.095,
    duration: 0.055,
    type: 'square'
  });
  playMoveTone({
    frequency: own ? 105 : 90,
    volume: 0.07,
    duration: 0.14,
    type: 'triangle',
    delay: 0.025
  });
}

/** Two-note alert, higher than a normal move click. */
export function playCheckSound() {
  playMoveTone({ frequency: 740, volume: 0.08, duration: 0.11, type: 'square' });
  playMoveTone({
    frequency: 988,
    volume: 0.07,
    duration: 0.16,
    type: 'triangle',
    delay: 0.1
  });
}

/**
 * Quiet move, capture, or check — check wins when a capture also gives check.
 * `own` only affects quiet/capture timbre.
 */
export function playBoardMoveSound(move, { own = true } = {}) {
  const san = typeof move === 'string' ? move : move?.san;
  if (typeof san === 'string' && san.endsWith('+')) {
    playCheckSound();
    return 'check';
  }
  if (typeof san === 'string' && san.includes('x')) {
    playCaptureSound({ own });
    return 'capture';
  }
  if (own) {
    playOwnMoveSound();
    return 'move';
  }
  playOpponentMoveSound();
  return 'move';
}

function playBrassNote(audio, { frequency, startTime, duration, volume = 0.11 }) {
  const primary = audio.createOscillator();
  const overtone = audio.createOscillator();
  const gain = audio.createGain();
  const filter = audio.createBiquadFilter();

  primary.type = 'sawtooth';
  overtone.type = 'triangle';
  primary.frequency.setValueAtTime(frequency, startTime);
  overtone.frequency.setValueAtTime(frequency * 2, startTime);

  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(frequency * 1.8, startTime);
  filter.Q.setValueAtTime(2.4, startTime);

  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.025);
  gain.gain.setValueAtTime(volume * 0.82, startTime + duration * 0.55);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  primary.connect(filter);
  overtone.connect(filter);
  filter.connect(gain);
  gain.connect(audio.destination);

  primary.start(startTime);
  overtone.start(startTime);
  primary.stop(startTime + duration + 0.04);
  overtone.stop(startTime + duration + 0.04);
}

/** Ceremonial trumpet fanfare when a ladder challenge arrives. */
export function playIncomingChallengeFanfare() {
  const audio = getAudioContext();
  if (!audio) {
    return;
  }

  const base = audio.currentTime + 0.02;
  const fanfare = [
    [392, 0.24, 0, 0.1],
    [494, 0.24, 0.2, 0.105],
    [587, 0.24, 0.4, 0.11],
    [784, 0.58, 0.6, 0.13],
    [988, 0.42, 1.05, 0.095]
  ];

  fanfare.forEach(([frequency, duration, offset, volume]) => {
    playBrassNote(audio, {
      frequency,
      startTime: base + offset,
      duration,
      volume
    });
  });
}
